/**
 * Legacy 12-digit SKU composition.
 *
 * SKU = vendor(2) + category(2) + base(2) + model(2) + size(2) + color(2)
 *
 * Extracted from UnifiedCreateWizard.jsx on 2026-08-24. It used to live inside
 * the wizard's matrix-building effect, which sourced every segment from the
 * currently ACTIVE axes only. When the reconcile flow was restricted to the
 * colour axis (commit 3e2fc8105), model and size silently became the empty
 * string and every generated SKU came out 8 digits long.
 *
 * Three rules. The second and third are the ones that cost two grill rounds:
 *
 *  1. A kind with no active axis inherits its two digits from the product's
 *     existing SKU. A kind with neither yields no SKU and says which.
 *
 *  2. vendor / category / base belong to the PRODUCT, not to the form. The
 *     category dropdown is fed by /api/sku-category-mappings, which filters
 *     `overflow_of IS NULL` (backend/app.py:3915), and the allocator lookup
 *     that returns the real bucket is mode='new'-only (UnifiedCreateWizard
 *     .jsx:706). The reconcile route validates only `len(sku) != 12`
 *     (backend/products_v2.py:1302) and never recomposes, so a form-composed
 *     SKU reaches Shopify, shp_products and LSM intact.
 *
 *     Measured against prod on 2026-08-24: of 314 twelve-digit SKUs, 61
 *     disagree with the mapping for their category_name — 36 of them because
 *     `category_name` is NULL, where the old `''.padStart(2,'0')` produced a
 *     plausible '00'. This is not a hypothetical.
 *
 *  3. Inheritance requires a REAL legacy SKU. Accepting any number that
 *     merely contains twelve digits (a 13-digit EAN, a supplier number) would
 *     mine six plausible pairs out of an unrelated identifier and hand rule 2
 *     the same failure it exists to prevent, only from the other side.
 *     Measured across all 311 live variants: strict `^[0-9]{12}$` and loose
 *     "contains >= 12 digits" both match 300. The guard costs nothing.
 *
 * This module READS. It never proposes a change to an existing SKU — the
 * owner's constraint of 2026-08-24 is that we repair the tool, not the stock.
 *
 * See docs/knowledge/operations/data-quirks.md §"SKU positional encoding is
 * NOT load-bearing" and docs/superpowers/specs/2026-08-24-variant-axis-
 * alignment-design.md §3.1.
 */

export const SKU_LEN = 12;

/** The three variable segments, in SKU order. NOT the Shopify option position
 *  order — the catalogue contains a product where the two disagree. */
export const AXIS_KINDS = ['model', 'size', 'color'];

/** The three leading segments, in SKU order. */
export const FIXED_KINDS = ['vendor', 'category', 'base'];

/** Der Praefix, unter dem `backend/sku_issuance.py` bedeutungslose Nummern
 *  vergibt. Muss mit `OPAQUE_PREFIX` dort uebereinstimmen — es gibt keinen
 *  gemeinsamen Ort fuer beide, deshalb steht es hier ausdruecklich. */
export const OPAQUE_PREFIX = '90';

/** A segment is exactly two digits. No padding, no truncation: a value that is
 *  not already well-formed is a MISSING value, not a fixable one. Callers must
 *  hand over raw form values — a `pad2('')` upstream would turn an absent
 *  category into a plausible '00'. */
export const isSegment = (value) => /^[0-9]{2}$/.test(String(value ?? ''));

/**
 * Split an existing legacy SKU into its six segments.
 * Returns null unless the WHOLE value is exactly twelve digits (rule 3 above).
 * Callers must read null as "nothing to inherit", never as zeros.
 *
 * @param {string|null|undefined} rawSku
 * @returns {{vendor: string, category: string, base: string,
 *            model: string, size: string, color: string} | null}
 */
export const extractSkuSegments = (rawSku) => {
  const s = String(rawSku ?? '');
  if (!/^[0-9]{12}$/.test(s)) return null;
  // Stufe B: eine Nummer aus dem reservierten Vergabe-Praefix hat KEINE
  // Segmente — die Stellen 3-4 sind blosse Zaehlerziffern. Wer sie trotzdem
  // zerlegt, komponiert daraus `900000` + Schwanz und trifft damit eine
  // Nummer, die `allocate_opaque_sku` spaeter selbst ausgibt. Die Kollision
  // waere still (`sync_products` degradiert eine doppelte SKU zu `sku=NULL`).
  if (s.startsWith(OPAQUE_PREFIX)) return null;
  return {
    vendor: s.slice(0, 2),
    category: s.slice(2, 4),
    base: s.slice(4, 6),
    model: s.slice(6, 8),
    size: s.slice(8, 10),
    color: s.slice(10, 12),
  };
};

/**
 * Map the wizard's state onto resolveVariantSku's arguments.
 *
 * This exists as its own exported function so the wiring is testable. The
 * defect this module was written for was never a wrong formula — it was a call
 * site that stopped supplying two of the six inputs.
 *
 * Reports rather than throws on a miswired `skuSegments`. There is no
 * ErrorBoundary above the wizard (App.js:305 mounts it bare), so a throw out of
 * the matrix effect unmounts the whole React root — a white screen for the
 * entire app. A named reason is just as loud where it matters: 'sku ready'
 * never renders.
 *
 * @param {object} args
 * @param {'new'|'variants'|'basic'} args.mode
 * @param {Array<{kind: string}>} args.activeAxes  axes the operator is filling in
 * @param {object} args.axisCodes                  {model?, size?, color?} mapping codes
 * @param {{vendor: string, category: string, base: string}} args.form  RAW, unpadded
 * @param {object|null} args.skuSegments  extractSkuSegments of the target product
 */
export const buildResolverArgs = ({ mode, activeAxes = [], axisCodes = {}, form, skuSegments }) => {
  const misconfigured = skuSegments != null && typeof skuSegments !== 'object';
  return {
    axisCodes,
    activeKinds: activeAxes.map((axis) => axis.kind),
    inherited: !misconfigured && mode === 'variants' ? (skuSegments || null) : null,
    form,
    misconfigured,
  };
};

/**
 * Compose one variant SKU.
 *
 * @param {object} args — the shape returned by buildResolverArgs
 * @returns {{sku: string,
 *            reason: null | {code: string, segment: string|null},
 *            drift: Array<{segment: string, form: string, product: string}>}}
 */
export const resolveVariantSku = ({
  axisCodes = {},
  activeKinds = [],
  inherited = null,
  form = {},
  misconfigured = false,
}) => {
  const drift = [];
  const fixed = {};

  if (misconfigured) {
    return { sku: '', reason: { code: 'resolver_misconfigured', segment: null }, drift };
  }

  for (const segment of FIXED_KINDS) {
    const fromForm = form[segment];
    const fromProduct = inherited ? inherited[segment] : null;

    // The product wins whenever it has an opinion (rule 2 in the header).
    const chosen = isSegment(fromProduct) ? fromProduct : fromForm;
    if (!isSegment(chosen)) {
      return { sku: '', reason: { code: 'fixed_segment_invalid', segment }, drift };
    }
    if (isSegment(fromProduct) && isSegment(fromForm) && fromForm !== fromProduct) {
      drift.push({ segment, form: String(fromForm), product: String(fromProduct) });
    }
    fixed[segment] = String(chosen);
  }

  const active = new Set(activeKinds);
  const variable = [];
  for (const kind of AXIS_KINDS) {
    if (active.has(kind)) {
      const code = axisCodes[kind];
      if (!isSegment(code)) {
        return { sku: '', reason: { code: 'axis_code_missing', segment: kind }, drift };
      }
      variable.push(String(code));
      continue;
    }
    const carried = inherited ? inherited[kind] : null;
    if (!isSegment(carried)) {
      return { sku: '', reason: { code: 'segment_unresolved', segment: kind }, drift };
    }
    variable.push(String(carried));
  }

  return {
    sku: `${fixed.vendor}${fixed.category}${fixed.base}${variable.join('')}`,
    reason: null,
    drift,
  };
};


/** Der Shopify-Achsenname → POG-Kind. Bewusst hier und nicht im Wizard: die
 *  Auswahl der Achsen ist eine REGEL, und eine Regel gehoert dorthin, wo eine
 *  Probe sie erreichen kann. */
export const optionAxisKind = (label) => {
  if (!label) return null;
  const lower = String(label).toLowerCase();
  if (/(model|modell)/i.test(lower)) return 'model';
  if (/(size|capacity|größe|grösse|groesse)/i.test(lower)) return 'size';
  if (/(colou?r|farbe)/i.test(lower)) return 'color';
  return null;
};

/**
 * Welche Achsen der Wizard im Varianten-Modus zeigt.
 *
 * 🔴 Der Rueckfall auf `['color']` ist NICHT Kosmetik. Ein Platzhalterprodukt
 * (keine Optionen oder nur `Title`, 114 Stueck) und eine Achse ohne POG-Kind
 * (`Variante`, `Ausfuehrung`, 26 Stueck) liefern eine LEERE Liste — und daraus
 * folgt: keine Zeilen, `canNext` verlangt `rows.length > 0`, Weiter-Knopf tot,
 * kein einziges Auswahlfeld. Ohne den Rueckfall macht P2 81 Produkte bedienbar
 * und rund 140 unbedienbar.
 *
 * Vor P2 stand im Wizard unbedingt `['color']`. Genau dahin faellt es zurueck.
 *
 * @param {Array<{name: string}>|null|undefined} shopifyOptions Live-Optionen
 * @param {string[]} axisOrder Die POG-Kinds in ihrer Reihenfolge
 */
export const variantAxisKinds = (shopifyOptions, axisOrder) => {
  const live = axisOrder.filter(
    (kind) => (shopifyOptions || []).some((o) => optionAxisKind(o?.name) === kind),
  );
  return live.length ? live : ['color'];
};


/**
 * Die Werte einer Achse, vorbelegt aus dem LIVE-Zustand des Produkts.
 *
 * 🔴 Warum NICHT aus den SKU-Segmenten. Fuer das am 2026-08-28 gemeldete
 * Produkt traegt das Size-Segment `32`, und die Zuordnungstabelle macht daraus
 * `Unigroesse` — waehrend die lebende Achse `102mm` heisst. Seit der Wizard die
 * Live-Achsen spiegelt, GEHT dieser Wert an Shopify: `union_option_values`
 * haenge `Unigroesse` an die echte `Size`-Achse. Das Regel-16-Gate hat am
 * Bildschirm gemessen, dass 29 von 105 mehrachsigen Zeilen so einen Titel
 * bilden, den es in Shopify nicht gibt.
 *
 * Ein Live-Wert, den die Zuordnungstabelle nicht kennt, wird NICHT geraten —
 * er faellt weg, und der Bediener waehlt. Ein plausibler falscher Wert ist
 * schlimmer als keiner.
 *
 * @param {string[]} liveValues Die Werte der lebenden Achse
 * @param {Array<object>} mappingRows Die Zuordnungszeilen (z. B. colorMap)
 * @param {string} valueKey Der Feldname mit dem Klartextwert
 */
export const prefillFromLiveValues = (liveValues, mappingRows, valueKey) =>
  (liveValues || [])
    .map((w) => {
      const zeile = (mappingRows || []).find(
        (r) => String(r?.[valueKey] ?? '').toLowerCase() === String(w ?? '').toLowerCase(),
      );
      if (!zeile) return null;
      // 🔴 Der WERT kommt aus der LEBENDEN Achse, nur der CODE aus der Tabelle.
      //
      // Der Vergleich oben ist schreibweisen-unabhaengig, die Rueckgabe war es
      // nicht: sie gab die ZEILE DER TABELLE zurueck. Stand dort 'schwarz' und
      // lebte am Produkt 'Schwarz', ging 'schwarz' an Shopify -- das legt einen
      // ZWEITEN Optionswert daneben an, statt den vorhandenen zu treffen.
      // Gemessen am 2026-09-27 an der Farbe-Achse: danach trug sie vier Werte
      // (Schwarz, Weiss, schwarz, weiss).
      return { ...zeile, [valueKey]: String(w) };
    })
    .filter(Boolean);

/**
 * Der Preis, den eine NEUE Variante von ihren Geschwistern erbt.
 *
 * Bei mehreren verschiedenen Preisen gewinnt der HOECHSTE — Entscheidung des
 * Eigentuemers vom 2026-08-28, und die Begruendung steht in der Richtung des
 * Fehlers: zu teuer faellt beim Ansehen auf, zu billig wird gekauft.
 *
 * 🔴 Ein Preis von 0 vererbt sich NICHT. Sonst wuerde sich genau der Fehler
 * fortpflanzen, gegen den diese Aenderung gebaut ist: am 2026-08-28 lief eine
 * 0-Euro-Variante in den laufenden Shop, und die naechste haette sie geerbt.
 *
 * Gibt `''` zurueck, wenn es nichts zu erben gibt — nie `'0.00'`, denn das
 * waere eine Aussage statt einer Leerstelle.
 */
export const preisVonGeschwistern = (variants) => {
  const preise = (variants || [])
    .map((v) => Number.parseFloat(v?.price))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!preise.length) return '';
  return Math.max(...preise).toFixed(2); // format-guard: ok — Formularwert (füllt ein Preisfeld vor), maschinenlesbar mit `.`
};
